import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Download, X, Smartphone } from 'lucide-react';

export const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Already installed, don't show banner
    }

    // For iOS, show instructions after delay
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('pwa-ios-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
      return;
    }

    // For Android/Desktop, listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem(isIOS ? 'pwa-ios-dismissed' : 'pwa-install-dismissed', 'true');
  };

  if (!showInstallBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 rounded-xl shadow-2xl z-50 animate-slide-up" dir="rtl">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 left-2 p-1 hover:bg-white/20 rounded-full transition"
        >
          <X size={18} />
        </button>
        
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-3 rounded-xl">
            <Smartphone className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold font-cairo text-lg">تثبيت التطبيق</h3>
            <p className="text-sm text-blue-100 font-cairo">
              {isIOS 
                ? 'أضف التطبيق للشاشة الرئيسية للوصول السريع'
                : 'ثبّت التطبيق للوصول السريع بدون متصفح'
              }
            </p>
          </div>
        </div>
        
        <Button 
          onClick={handleInstall}
          className="w-full mt-3 bg-white text-blue-700 hover:bg-blue-50 font-cairo font-bold"
        >
          <Download className="ml-2" size={18} />
          {isIOS ? 'عرض التعليمات' : 'تثبيت الآن'}
        </Button>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowIOSInstructions(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" dir="rtl" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold font-cairo text-gray-900 mb-4 text-center">
              تثبيت التطبيق على iPhone
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center font-bold shrink-0">1</div>
                <p className="font-cairo text-gray-700">
                  اضغط على زر <strong>المشاركة</strong> (المربع مع السهم للأعلى) في أسفل الشاشة
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center font-bold shrink-0">2</div>
                <p className="font-cairo text-gray-700">
                  مرر للأسفل واختر <strong>"إضافة إلى الشاشة الرئيسية"</strong>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center font-bold shrink-0">3</div>
                <p className="font-cairo text-gray-700">
                  اضغط <strong>"إضافة"</strong> في الزاوية العلوية
                </p>
              </div>
            </div>
            
            <Button 
              onClick={() => {
                setShowIOSInstructions(false);
                handleDismiss();
              }}
              className="w-full mt-6 font-cairo"
            >
              فهمت
            </Button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default InstallPWA;
